import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertType } from 'interfaces';
import { setAlert, showAlert } from 'redux/modules/Alert/alert';
import { getUser } from 'redux/modules/Authorization/authSelectors';
import { useAppDispatch, useAppSelector } from 'redux/hooks';
import { useAPlayerStatus } from 'hooks/useAPlayerStatus/useAPlayerStatus';
import { getAPlayerState, type APlayerState } from 'services/aPlayer';

export const useInviteGuest = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const authUser = useAppSelector(getUser);
  const isAPlayer = useAPlayerStatus();

  const [aPlayerState, setAPlayerState] = useState<APlayerState | null>(null);
  const [loading, setLoading] = useState(true);
  const [showInviteModal, setShowInviteModal] = useState(false);

  const fetchState = useCallback(async () => {
    if (!authUser?.id || !isAPlayer) return;
    const data = await getAPlayerState(authUser.id);
    setAPlayerState(data);
    setLoading(false);
  }, [authUser?.id, isAPlayer]);

  const reloadState = useCallback(async () => {
    await fetchState();
    window.dispatchEvent(new CustomEvent('aplayer-state-changed'));
  }, [fetchState]);

  useEffect(() => {
    fetchState();
  }, [fetchState]);

  useEffect(() => {
    window.addEventListener('aplayer-state-changed', fetchState);
    return () =>
      window.removeEventListener('aplayer-state-changed', fetchState);
  }, [fetchState]);

  const handleInvite = useCallback(() => {
    if (aPlayerState?.activeGuest) {
      dispatch(
        setAlert({
          type: AlertType.error,
          title: t('aPlayerPage.inviteAgain.conflictTitle'),
          bodyText: '',
        }),
      );
      dispatch(showAlert());
      return;
    }

    if (
      aPlayerState &&
      aPlayerState.invitationInfo.invitesUsedThisQuarter >= 2
    ) {
      dispatch(
        setAlert({
          type: AlertType.error,
          title: t('aPlayerPage.inviteAgain.quarterLimitTitle'),
          bodyText: '',
        }),
      );
      dispatch(showAlert());
      return;
    }

    setShowInviteModal(true);
  }, [aPlayerState, dispatch, t]);

  return {
    aPlayerState,
    loading,
    reloadState,
    handleInvite,
    showInviteModal,
    closeModal: () => setShowInviteModal(false),
  };
};
